import moment from "moment";
import { useEffect, useState } from "react";
import { Cell } from "react-table";
import { getContractApply } from "src/api/contract/contract-api";
import { ContractManageList } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { ContractVisitorListModel } from "src/api/visitor/visitor-types";
import { WorkOrderDetailModel } from "src/api/work-order/workorder-types";
import GoToListButton from "src/components/GoToListButton";
import InnerTable from "src/components/InnerTable";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import Link from "src/components/PartnerLink";
import { PagePath } from "src/pages/product/details";
import useWorkOrder from "src/pages/workOrder/hooks/useWorkOrder";
import { ViewYmdFormat, YmdFormat } from "src/utils";

type Props = {
  workOrder?: WorkOrderDetailModel;
};

const WorkOrderAccess = ({ workOrder }: Props) => {
  const [contractInfo, setContractInfo] = useState({
    spaceProductName: "",
    contractId: "",
  });

  const [contractVisitorList, setContractVisitorList] = useState<ContractVisitorListModel[]>([]);
  const { getVisitorsList } = useWorkOrder();

  const { executeAsync: getContractList } = useApiOperation(getContractApply);

  useEffect(() => {
    const fetchVisitorsListAndContract = async () => {
      const visitorsList = await getVisitorsList(String(workOrder?.id));

      setContractVisitorList(visitorsList || []);

      const contractManageId = visitorsList && visitorsList.length > 0 && visitorsList[0].contractManageId;

      //
      if (!contractManageId) return;

      const { data } = await getContractList({ contractManageIds: String(contractManageId) });
      const contract = data.data.content[0] as ContractManageList;

      setContractInfo({
        spaceProductName: contract.spaceProductName || "",
        contractId: String(contract.contractId),
      });
    };
    if (workOrder?.id) {
      fetchVisitorsListAndContract();
    }
  }, [workOrder]);

  const accessColumns = [
    {
      Header: "No",
      accessor: "contractVisitorId",
      width: 80,
      Cell: ({ row }: Cell<ContractVisitorListModel>) => {
        return <span>{row.index + 1}</span>;
      },
    },
    {
      Header: "작업자",
      accessor: "visitorName",
      width: 160,
    },
    {
      Header: "출입 기간",
      accessor: "visitStartTime",
      width: 300,
      Cell: ({ row }: Cell<ContractVisitorListModel>) => {
        const startDate = moment(row.original.visitStartTime).format(YmdFormat.YYYY_MM_DD);
        const endDate = moment(row.original.visitEndTime).format(YmdFormat.YYYY_MM_DD);

        return (
          <div className="d-flex">
            <div className="">
              <span>{moment(startDate).format(ViewYmdFormat.YYYY_MM_DD_WEEK)}</span>
            </div>
            <div className="mx5">~</div>
            <div>
              <span>{moment(endDate).format(ViewYmdFormat.YYYY_MM_DD_WEEK)}</span>
            </div>
          </div>
        );
      },
    },
    {
      Header: "출입 가능 시간",
      accessor: "",
      width: 120,
      Cell: ({ row }: Cell<ContractVisitorListModel>) => {
        const startTime = moment(row.original.visitStartTime).format(ViewYmdFormat.HH_MM);
        const endTime = moment(row.original.visitEndTime).format(ViewYmdFormat.HH_MM);
        return (
          <div>
            <span>{startTime}</span>
            <span className="mx5">~</span>
            <span>{endTime}</span>
          </div>
        );
      },
    },
    {
      Header: "상태",
      accessor: "visitStatus",
      width: 140,
      Cell: ({ row, value }: Cell<ContractVisitorListModel>) => {
        const VISITOR_STATUS: { [key: string]: { label: string; color: string } } = {
          VISIT_USE: { label: "사용", color: "status A" },
          VISIT_PAUSE: { label: "일시정지", color: "status B" },
          VISIT_DELETE: { label: "삭제", color: "status D" },
          VISIT_CLOSE: { label: "종료", color: "status D" },
        };
        let status = "";
        let color = "";
        for (const key in VISITOR_STATUS) {
          if (key === value) {
            status = VISITOR_STATUS[key].label;
            color = VISITOR_STATUS[key].color;
            break;
          }
        }
        if (row.original.isDeleted) {
          status = "삭제";
          color = "status D";
        }
        return <div className={color}>{status}</div>;
      },
    },
    {
      Header: "상세",
      accessor: "",
      width: 140,
      Cell: ({ row }: Cell<ContractVisitorListModel>) => {
        return (
          <>
            <Link
              target="_blank"
              to={PagePath.visitor.detail
                .replace(":visitApplyNumber", String(row.original.visitApplyNumber))
                .replace(":contractApplyNumber", row.original.contractApplyNumber || "")}
              className="text-underline"
            >
              바로가기
            </Link>
            <span className="ic-target-blank"></span>
          </>
        );
      },
    },
  ];
  return (
    <>
      <div className="contents-container__scroll">
        <div className="contents-container__wrap">
          <article className="contents-container__wrap-article">
            <BaseSectionTitle title={"출입 관리"} />
            <section className="contents-container__grid ">
              <div className="contents-container__grid-index">
                <p>신청/계약</p>
              </div>
              <div className="contents-container__grid-contents">
                {contractVisitorList.length > 0 ? (
                  <div className="flex-row flex-center-start">
                    <Link
                      to={PagePath.contract.detail.replace(":id", String(contractInfo.contractId)) + "?tab=accessLevel"}
                      target="_blank"
                      className="text-underline"
                    >
                      {contractInfo.spaceProductName}
                    </Link>
                    <span className="ic-target-blank"></span>
                  </div>
                ) : (
                  <div>-</div>
                )}
              </div>
            </section>
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>출입 스케줄</p>
              </div>
              <div className="contents-container__grid-contents">
                {contractVisitorList.length > 0 ? <InnerTable data={contractVisitorList} columns={accessColumns} /> : <div>-</div>}
              </div>
            </section>
          </article>
        </div>
      </div>

      <div className="contents-container__btn-wrap">
        <div className="left-area">
          <GoToListButton />
        </div>
      </div>
    </>
  );
};

export default WorkOrderAccess;
